import {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
  useMemo,
} from 'react';
import { type ContextInterface } from '@convertcom/js-sdk';

import convertSdk from '../sdk/convertSdkClient';

import { useCookies } from 'hooks';

import { useUserContext } from './UserContext';

import { PAGE } from '../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';
import { COOKIE } from '../helpers/constants';
import getConfig from 'next/config';
import { trackBucketing } from '../helpers/experiments/tracking/trackBucketing';
import { getGalleryInteractionsBucketedCookie } from '../helpers/experiments/galleryInteractionsExperiment.constants';
import { getStickySaveShareBucketedCookie } from '../helpers/experiments/stickySaveShareExperiment.constants';

type UseConvertContextParameters = { page: PAGE };

type ConvertProviderParameters = {
  children: ReactNode;
  galleryInteractionsVariationKey?: string | null;
  stickySaveShareVariationKey?: string | null;
};

const ConvertContext = createContext<{
  sdkContext: ContextInterface | null | undefined;
  galleryInteractionsVariationKey: string | null;
  stickySaveShareVariationKey: string | null;
} | null>(null);
/**
 * This function allows the Convert SDK context to be accessed anywhere within the application
 * in order to run A/B experiments that require code changes.
 *
 * This may include displaying different components and executing different functions
 * depending on variations within the experiment.
 */
const ConvertProvider = (params: ConvertProviderParameters) => {
  const {
    children,
    galleryInteractionsVariationKey = null,
    stickySaveShareVariationKey = null,
  } = params;

  const {
    publicRuntimeConfig: {
      GALLERY_INTERACTION_EXPERIMENT_KEY,
      STICKY_SAVE_SHARE_EXPERIMENT_KEY,
    },
  } = getConfig();

  const { get } = useCookies({ name: COOKIE.DD_CLIENT_ID });

  const ddClientId = get();

  const [sdkContext, setSdkContext] = useState<
    ContextInterface | null | undefined
  >();
  const {
    consentOptions: { allowConvert },
  } = useUserContext();

  useEffect(() => {
    const initSdk = async (userId: string) => {
      try {
        await convertSdk.onReady();
        const context = convertSdk.createContext(userId);
        setSdkContext(context);
      } catch (error) {
        setSdkContext(null);
        logger.error({
          error,
          message: 'Error loading Convert SDK',
          tags: [PAGE.APP],
        });
      }
    };
    if (
      allowConvert &&
      typeof ddClientId === 'string' &&
      ddClientId.trim().length > 0
    ) {
      initSdk(ddClientId);
    } else {
      setSdkContext(null);
    }
  }, [allowConvert, ddClientId]);

  useEffect(() => {
    if (!galleryInteractionsVariationKey) return;

    trackBucketing({
      cookieName: getGalleryInteractionsBucketedCookie(),
      event: 'experiment_bucketed',
      experimentKey: GALLERY_INTERACTION_EXPERIMENT_KEY,
      variationKey: galleryInteractionsVariationKey,
    });
  }, [galleryInteractionsVariationKey, GALLERY_INTERACTION_EXPERIMENT_KEY]);

  useEffect(() => {
    if (!stickySaveShareVariationKey) return;

    trackBucketing({
      cookieName: getStickySaveShareBucketedCookie(),
      event: 'experiment_bucketed',
      experimentKey: STICKY_SAVE_SHARE_EXPERIMENT_KEY,
      variationKey: stickySaveShareVariationKey,
    });
  }, [stickySaveShareVariationKey, STICKY_SAVE_SHARE_EXPERIMENT_KEY]);

  const contextValue = useMemo(
    () => ({
      sdkContext,
      galleryInteractionsVariationKey,
      stickySaveShareVariationKey,
    }),
    [sdkContext, galleryInteractionsVariationKey, stickySaveShareVariationKey],
  );

  return (
    <ConvertContext.Provider value={contextValue}>
      {children}
    </ConvertContext.Provider>
  );
};

const useConvertContext = (params: UseConvertContextParameters) => {
  const { page } = params;
  const context = useContext(ConvertContext);
  if (!context) {
    const error = new Error(
      'useConvertContext must be used within an instance of ConvertProvider',
    );
    logger.error({
      error,
      message:
        'useConvertContext must be used within an instance of ConvertProvider',
      tags: [page],
    });
    throw error;
  }
  return context;
};

export { ConvertProvider, useConvertContext, ConvertContext };
